// src/page/Terms.jsx
import { Link } from "react-router-dom";

export default function Terms() {
  const sections = [
    { id: "intro", label: "Important Notice" },
    { id: "platform", label: "Platform / Services" },
    { id: "consent", label: "Consent & Communications" },
    { id: "prohibited", label: "Prohibited Use" },
    { id: "ip", label: "Copyright & Trademark" },
    { id: "no-warranty", label: "No Warranty" },
    { id: "release", label: "Release" },
    { id: "limitation", label: "Limitation of Liability" },
    { id: "arbitration", label: "Arbitration & Class Waiver" },
    { id: "indemnification", label: "Indemnification" },
    { id: "termination", label: "Termination" },
    { id: "entire", label: "Entire Agreement & E-Sign" },
    { id: "revisions", label: "Electronic Comms & Revisions" },
    { id: "contact", label: "Contact" },
    { id: "updated", label: "Last Updated" },
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      {/* breadcrumb */}
      <nav className="text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-[#0A2540]">
          Home
        </Link>
        <span className="mx-2">›</span>
        <span className="text-slate-600">Legal</span>
        <span className="mx-2">›</span>
        <span className="text-[#0A2540] font-medium">Terms of Use</span>
      </nav>

      {/* header */}
      <header className="mb-8">
        <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#0A2540]/70">
          Legal
        </p>
        <h1 className="mt-2 text-center text-3xl md:text-4xl font-extrabold text-[#0A2540]">
          Terms of Use
        </h1>
        <p className="mt-3 text-center text-slate-600">
          Please read carefully. These terms govern your use of this platform
          and services.
        </p>
      </header>

      <div className="grid lg:grid-cols-[260px,1fr] gap-10">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-[#0A2540]">
                On this page
              </div>
              <ul className="p-3 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded px-3 py-2 text-slate-700 hover:bg-slate-50 hover:text-[#0A2540]"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* Article */}
        <article className="prose prose-slate max-w-none">
          <section id="intro" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Important Notice</h2>
            <div className="rounded-lg border-l-4 border-[#C89B3C] bg-[#FFF7E6] px-4 py-3">
              THIS AGREEMENT CONTAINS A MANDATORY ARBITRATION PROVISION, WAIVER
              OF JURY TRIAL, AND CLASS-ACTION WAIVER. IF YOU DO NOT AGREE,
              PLEASE DO NOT USE THIS SITE.
            </div>
            <p className="mt-3">
              By accessing or using this platform (including via telephone,
              mail, text, email, or other means), you accept and agree to be
              bound by these Terms and our{" "}
              <Link to="/privacy" className="underline text-[#0A2540]">
                Privacy Policy
              </Link>
              . MassTortPlaintiffs is a Wyoming limited liability company; this
              platform is intended for United States residents.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="platform" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Platform / Services</h2>
            <p>
              We provide marketing and technology services used by brands and
              participating attorneys for consent management and client
              acquisition/retention. Services through this platform are for the
              benefit of third parties only. We are not a law firm and do not
              provide legal advice.
            </p>
            <ul>
              <li>
                By submitting an inquiry, you authorize us to share your
                information so that our network, affiliates, or vendors may
                contact you by phone/text/email (including autodialed or
                prerecorded).
              </li>
              <li>
                We do not charge you a fee to use the platform. Qualification
                criteria for services/products are set by the third-party
                providers who may contact you directly.
              </li>
              <li>
                This platform is for U.S. residents and is not intended for use
                by minors.
              </li>
            </ul>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="consent" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Consent & Communications</h2>
            <p>
              By submitting your information, you provide express written
              consent to be contacted at the numbers/emails you provide,
              including by automated technology. Consent is not a condition of
              purchase and may be revoked at any time.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="prohibited" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Prohibited Use</h2>
            <p>Among other things, you agree not to:</p>
            <ul>
              <li>
                Use the platform for unlawful purposes or to transmit
                unlawful/abusive content.
              </li>
              <li>
                Interfere with the platform’s security, integrity, or
                operations.
              </li>
              <li>
                Scrape, copy, or monitor pages/content without written
                permission.
              </li>
              <li>
                Impose unreasonable load on infrastructure or attempt to bypass
                protections.
              </li>
              <li>
                Submit data you do not have a right to distribute or that
                infringes others’ rights.
              </li>
              <li>
                Engage in fraud, impersonation, or other deceptive practices.
              </li>
            </ul>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="ip" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Copyright & Trademark</h2>
            <p>
              We (and others) own intellectual property on or available through
              this platform, including copyrights, trademarks, and other rights.
              You may print individual screens for personal, non-commercial
              records, but you may not otherwise copy, modify, publish, display,
              transmit, or exploit content without prior written consent.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="no-warranty" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">No Warranty</h2>
            <p>
              THE PLATFORM AND ALL RELATED CONTENT/SERVICES ARE PROVIDED “AS IS”
              AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
              EXPRESS OR IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="release" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Release</h2>
            <p>
              You release MassTortPlaintiffs and its service providers,
              affiliates, officers, directors, employees, and agents from any
              and all claims, liabilities, and damages arising from or related
              to your use of the platform and services.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="limitation" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR USE. ANY CAUSE OF ACTION
              MUST BE BROUGHT WITHIN ONE (1) YEAR AFTER IT ACCRUES.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="arbitration" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Arbitration Agreement & Class Action Waiver
            </h2>

            <details className="rounded-lg border border-slate-200 p-4">
              <summary className="cursor-pointer font-semibold text-[#0A2540]">
                Read the arbitration terms
              </summary>
              <div className="mt-3 space-y-3 text-[15px] leading-7">
                <p>
                  You and MassTortPlaintiffs agree that any disputes arising out
                  of or relating to these Terms or your use of the Services will
                  be resolved by binding arbitration on an individual basis,
                  administered by JAMS under its consumer rules. The Federal
                  Arbitration Act governs this agreement.
                </p>
                <p>
                  The arbitrator (not a court) has exclusive authority to
                  resolve any dispute regarding the interpretation,
                  applicability, or enforceability of this arbitration
                  agreement.
                </p>
                <p>
                  <strong>Class Waiver:</strong> You and we agree that each may
                  bring claims only in an individual capacity and not as a class
                  member or representative in any purported class or
                  representative proceeding.
                </p>
                <p>
                  <strong>Severability:</strong> If any part of this arbitration
                  agreement is found unenforceable, the remainder will continue
                  to apply (except that if the class waiver is found
                  unenforceable, this entire arbitration agreement is void).
                </p>
                <p>
                  <strong>Costs:</strong> Filing, administration, and arbitrator
                  fees are governed by JAMS rules.
                </p>
                <p>
                  <strong>Changes:</strong> If we change this arbitration
                  agreement in the future, such change will not apply to claims
                  filed before the effective date of the change.
                </p>
              </div>
            </details>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="indemnification" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Indemnification</h2>
            <p>
              You agree to indemnify and hold MassTortPlaintiffs, its
              subsidiaries, affiliates, officers, directors, employees, agents,
              contractors, and service providers harmless from any claims or
              demands, including reasonable attorneys’ fees, arising from your
              use of the platform or violation of these Terms.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="termination" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Termination</h2>
            <p>
              We may suspend or terminate access to the platform at any time,
              with or without notice, for any reason, including violation of
              these Terms. We may also modify or discontinue the Services at any
              time.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="entire" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Entire Agreement & E-Sign Consent
            </h2>
            <p>
              These Terms, together with the{" "}
              <Link to="/privacy" className="underline text-[#0A2540]">
                Privacy Policy
              </Link>
              , constitute the entire agreement between you and us. By using the
              platform, you consent to receive electronic communications and
              agree that your electronic signature has the same effect as a
              physical signature.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="revisions" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Electronic Communications & Revisions
            </h2>
            <p>
              When you access or email us, you consent to receive communications
              electronically. We reserve the right to change features or these
              Terms at any time. We’ll indicate changes by updating the date
              below. Continued use after changes indicates acceptance.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="contact" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Contact</h2>
            <p>
              Email:{" "}
              <a
                href="mailto:info@masstortplaintiffs.com"
                className="underline text-[#0A2540]"
              >
                info@masstortplaintiffs.com
              </a>
              <br />
              Mail: PO Box 214, Oceanside, NY 11572
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="updated" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Last Updated</h2>
            <p>April 30, 2025</p>
          </section>

          {/* Newsletter strip */}
          <section className="mt-12 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-[#0A2540]">
                  Sign Up For Our Newsletter
                </h3>
                <p className="text-slate-600">
                  Get new case updates, settlement deadlines, and breaking legal
                  news.
                </p>
              </div>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex w-full max-w-lg gap-2"
              >
                <input
                  type="text"
                  required
                  placeholder="First name *"
                  className="w-1/3 flex-1 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <input
                  type="email"
                  required
                  placeholder="Email address *"
                  className="w-1/2 flex-1 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-4 py-2 font-semibold text-white hover:bg-[#B8872D] transition"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Z" />
                    <path d="M12 3a7 7 0 0 0-7 7v4l-1.5 1.5a1 1 0 0 0 .7 1.7h15.6a1 1 0 0 0 .7-1.7L19 14v-4a7 7 0 0 0-7-7Z" />
                  </svg>
                  Subscribe
                </button>
              </form>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
